import React, { useCallback, useState } from 'react';
import type { FormikProps } from 'formik';
import styled, { css } from 'styled-components';

import { Row, Alert, Button } from 'components/bootstrap';
import useUserDateTime from 'hooks/useUserDateTime';
import type { DataLakeSearchType } from 'data-lake/preview/Types';
import { onInitializingTimerange } from 'views/components/TimerangeForForm';
import { WIDGET_DEFAULTS, HIDE_DL_SEARCH_EXPIRED_RESULT_ALERT } from 'data-lake/preview/Constants';
import type Search from 'views/logic/search/Search';
import type { FormValues } from 'data-lake/preview/SearchBar';
import { TELEMETRY_EVENT_TYPE } from 'telemetry/Constants';
import useSendTelemetry from 'logic/telemetry/useSendTelemetry';
import Store from 'logic/local-storage/Store';

const Container = styled.div`
  display: flex;
  justify-content: space-between;
`;

const StyledAlert = styled(Alert)`
  margin-top: 0;
`;
const StyledButton = styled(Button)(
  ({ theme }) => css`
    margin-right: ${theme.spacings.xs};
  `,
);

type Props = {
  expiredSearch: Search;
  searchBarFormRef: React.RefObject<FormikProps<FormValues>>;
};

const ExpiredSearchInfo = ({ expiredSearch, searchBarFormRef }: Props) => {
  const { formatTime } = useUserDateTime();
  const sendTelemetry = useSendTelemetry();
  const [hideExplanation, setHideExplanation] = useState(Store.get(HIDE_DL_SEARCH_EXPIRED_RESULT_ALERT) === 'true');

  const hideFeatureExplanation = useCallback(() => {
    Store.set(HIDE_DL_SEARCH_EXPIRED_RESULT_ALERT, 'true');
    setHideExplanation(true);
  }, []);

  const restartExpiredSearch = useCallback(() => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.DATALAKE.PREVIEW.APPLY_FILTER_FROM_EXPIRED_SEARCH, {
      app_pathname: '/data-storage/preview',
      app_section: 'expired-info',
    });
    const searchType = expiredSearch.queries.first().searchTypes[0] as DataLakeSearchType;
    searchBarFormRef.current?.setValues({
      stream: searchType.streams[0],
      timerange: onInitializingTimerange(searchType.timerange, formatTime),
      fields: {
        fieldFilters: searchType.field_filters ?? WIDGET_DEFAULTS.fieldFilters,
        operator: searchType.filters_operator ?? WIDGET_DEFAULTS.fieldsOperator,
      },
    });
  }, [expiredSearch.queries, formatTime, searchBarFormRef, sendTelemetry]);

  if (hideExplanation) {
    return null;
  }

  return (
    <Row>
      <StyledAlert bsStyle="default" onDismiss={hideFeatureExplanation}>
        <Container>
          <span>
            Your previous search has expired. Search results are stored for 24 hours and have now been cleared. You can
            reapply the previous filters or start a completely new search.
          </span>
          <StyledButton bsSize="xsmall" bsStyle="info" onClick={restartExpiredSearch}>
            Reapply filters
          </StyledButton>
        </Container>
      </StyledAlert>
    </Row>
  );
};

export default ExpiredSearchInfo;
